import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom';
import recordingsService from '../../services/recordings.service';
import { Loader } from '@progress/kendo-react-indicators';
import { SweetAlertOptions } from 'sweetalert2';
import useSwal from '../../hooks/useSwal';
import { AxiosError } from 'axios';
import { Button } from '@progress/kendo-react-buttons';
import useTranslation from '../../hooks/useTranslation';
import useLocale from '../../hooks/useLocale';

const BulkDownload = () => {
    const trans = useTranslation("BulkDownload");
    const localeCtx = useLocale();
    const { requestId } = useParams();
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const navigate = useNavigate();
    const swal = useSwal();

    useEffect(() => {
        if (!localeCtx?.selectedLocale?.current.componentTranslations["BulkDownload"]) {
            trans.fetchTranslations("BulkDownload");
        }
    }, [localeCtx?.selectedLocale]);

    useEffect(() => {
        if (requestId) {
            downloadExcel();
        } else {
            setError(true);
            setLoading(false);
        }
    }, [requestId]);

    const downloadExcel = async () => {
        try {
            setLoading(true);
            setError(false);

            const response = await recordingsService.bulkdownloadV2Downlaod(requestId ?? "");
            const blob = response.data; // since we set responseType: 'blob'

            const disposition = response.headers['content-disposition'];
            let fileName = "download.zip"; // fallback

            if (disposition) {
                const fileNameMatch = disposition.match(/filename\*?=(?:UTF-8''|")(.*?)(?:;|$|")/);
                if (fileNameMatch && fileNameMatch.length > 1) {
                    fileName = decodeURIComponent(fileNameMatch[1]);
                }
            }

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            if (err instanceof AxiosError) {
                let errorMessage = trans.fetchLabelKeyTranslation("BulkDownloadFailedError", "Bulk download failed.");

                try {
                    // Convert blob to text and then parse as JSON
                    const errorText = await err.response?.data.text();
                    const errorJson = JSON.parse(errorText);
                    errorMessage = errorJson?.error || errorMessage;
                } catch (parseErr) {
                }
                let translatedErrorMessage = errorMessage;
                if (errorMessage.includes("No details exist for this requestId")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("NoDetailsExistError", "No details exist for this requestId. Please provide a valid requestId.");
                }
                else if (errorMessage.includes("File has been deleted")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("FileDeletedError", "File has been deleted.");
                }
                else if (errorMessage.includes("User is not allowed")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("UserNotAllowedError", "User is not allowed to download the file.");
                }
                else if (errorMessage.includes("Invalid storage location")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("InvalidStorageError", "Invalid storage location");
                }
                else if (errorMessage.includes("Failed to download the file from storage")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("FailedToDownloadFromStorageError", "Failed to download the file from storage.");
                }
                else if (errorMessage.includes("Download response is either null")) {
                    translatedErrorMessage = trans.fetchLabelKeyTranslation("DownloadResponseError", "Download response is either null or unsuccessful.");
                }

                const swalOptions: SweetAlertOptions = {
                    icon: "error",
                    title: trans.fetchLabelKeyTranslation("SwtAltErrorTitle", "Error"),
                    text: translatedErrorMessage,
                };
                swal.fire(swalOptions);
            }
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ textAlign: "center", marginTop: "50px" }}>
            {loading && (
                <div>
                    <Loader size="large" type="infinite-spinner" />
                    <p>{trans.fetchLabelKeyTranslation("DownloadingText", "Downloading your file, please wait...")}</p>
                </div>
            )}

            {!loading && error && (
                <div>
                    <p style={{ color: "red" }}>
                        {trans.fetchLabelKeyTranslation("RetryDownloadMsg", "Failed to download file. Please try again later.")}
                    </p>
                </div>
            )}

            {!loading && !error && (
                <>
                    <p style={{ color: 'green' }}>{trans.fetchLabelKeyTranslation("SuccessfulDownloadMsg", "Your file has been downloaded successfully.")}</p>
                    <Button
                        onClick={() => navigate("/home")}
                        className={
                            "btn bg-primary text-white fs-13 fw-normal"
                        }
                        style={{ height: "29px", margin: "-1px 0 0 0" }}
                        disabled={loading}
                    >
                        {trans.fetchLabelKeyTranslation("HomePageMsg", "Go back to Home Page")}

                    </Button>
                </>
            )
            }

        </div >
    );
}

export default BulkDownload;